/**
 * Card Details Panel - Slide-out panel showing comprehensive card information
 */
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { IoPencil } from 'react-icons/io5';

import CardAvailabilitySection from '@/components/CardDetails/CardAvailabilitySection';
import CardDetailsPanelLayout from '@/components/CardDetails/CardDetailsPanelLayout';
import CardDetailsScryfallLink from '@/components/CardDetails/CardDetailsScryfallLink';
import CardShowcase from '@/components/CardDetails/CardShowcase';
import ConfirmActionModal from '@/components/CardDetails/ConfirmActionModal';
import LegalitiesSection from '@/components/CardDetails/LegalitiesSection';
import PricesSection from '@/components/CardDetails/PricesSection';
import QuickActionsBar from '@/components/CardDetails/QuickActionsBar';
import RulingsSection from '@/components/CardDetails/RulingsSection';
import Spinner from '@/components/Spinner/Spinner';
import useCardIndex from '@/hooks/useCardIndex';
import { getOracleId } from '@/lib/cardUtils';
import type { EnrichedCollectionCard } from '@/types/card';
import { useCollectionQuery, useDeleteCardMutation, useMoveToFolderMutation } from '../../hooks/api';

import useSelectedCard from '../../hooks/useSelectedCard';
import EditCollectionCardModal from '../EditCollectionCardModal';
import FolderPickerModal from './FolderPickerModal';
import InstanceInfoSection from './InstanceInfoSection';

export default function CardDetailsPanel(): ReactElement | null {
  const { selectedCardId, clearSelection } = useSelectedCard();
  const { data: cards } = useCollectionQuery();
  const { getCardById, getAllPrintingsByName } = useCardIndex();
  const [editPhase, setEditPhase] = useState<'details' | 'edition' | null>(null);
  const [showFolderPicker, setShowFolderPicker] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Mutations
  const moveMutation = useMoveToFolderMutation();
  const deleteMutation = useDeleteCardMutation();

  // Find the selected card from collection
  const selectedCard: EnrichedCollectionCard | undefined = useMemo(() => {
    if (!selectedCardId || !cards) return undefined;
    return cards.find((c) => c.id === selectedCardId);
  }, [selectedCardId, cards]);

  const indexCard = selectedCard ? getCardById(selectedCard.scryfall_id) : null;
  const isInCardIndex = Boolean(indexCard);
  const indexCardName = indexCard?.name;
  const hasOtherPrintings = useMemo(
    () => (indexCardName ? getAllPrintingsByName(indexCardName).length > 1 : false),
    [indexCardName, getAllPrintingsByName],
  );

  // Reset modals when card changes
  useEffect(() => {
    setEditPhase(null);
    setShowFolderPicker(false);
    setShowDeleteConfirm(false);
  }, [selectedCardId]);

  // Handler: Move to folder
  const handleMoveToFolder = async (folderId: string | null) => {
    if (!selectedCard) return;
    try {
      await moveMutation.mutateAsync({
        cardIds: [selectedCard.id],
        folderId,
      });
      setShowFolderPicker(false);
    } catch (error) {
      console.error('Failed to move card:', error);
    }
  };

  // Handler: Delete card
  const handleDelete = async () => {
    if (!selectedCard) return;
    try {
      await deleteMutation.mutateAsync(selectedCard.id);
      setShowDeleteConfirm(false);
      clearSelection();
    } catch (error) {
      console.error('Failed to delete card:', error);
    }
  };

  return (
    <CardDetailsPanelLayout
      isOpen={selectedCardId !== null}
      onClose={clearSelection}
      backLabel="Back to Collection"
      ariaLabelledBy="card-details-title"
    >
      {/* Quick Actions Bar - Star, List, Deck (always visible at top) */}
      {selectedCard && (
        <div className="border-storm shrink-0 border-b">
          <QuickActionsBar
            oracleId={getOracleId(selectedCard.card)}
            cardName={selectedCard.card.name}
            card={selectedCard.card}
            scryfallId={selectedCard.scryfall_id}
          />
        </div>
      )}

      {/* Content */}
      {!selectedCard ? (
        <div className="flex flex-1 items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : (
        <>
          <div className="flex-1 overflow-y-auto">
            {/* Card Showcase (Hero) */}
            <CardShowcase
              card={selectedCard}
              onChangeEdition={hasOtherPrintings && isInCardIndex ? () => setEditPhase('edition') : undefined}
            />

            <InstanceInfoSection card={selectedCard} />

            {/* Availability Section - per-edition inventory vs deck usage */}
            <CardAvailabilitySection
              oracleId={getOracleId(selectedCard.card) ?? undefined}
              cardName={selectedCard.card.name}
              selectedScryfallId={selectedCard.scryfall_id}
            />

            {/* Prices Section */}
            <PricesSection card={selectedCard} />

            {/* Rulings Section */}
            <RulingsSection cardId={selectedCard.scryfall_id} />

            {/* Legalities Section */}
            <LegalitiesSection card={selectedCard.card} />
          </div>

          {/* Actions Bar */}
          <footer className="border-storm flex h-16 shrink-0 items-center justify-between border-t px-4">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setEditPhase('details')}
                disabled={!isInCardIndex}
                title={isInCardIndex ? undefined : 'Card data is still loading'}
                className="text-silver hover:text-pearl hover:bg-slate focus-ring flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50"
              >
                <IoPencil className="h-4 w-4" />
                Edit
              </button>
              <button
                type="button"
                onClick={() => setShowFolderPicker(true)}
                className="text-silver hover:text-pearl hover:bg-slate focus-ring rounded-lg px-3 py-2 text-sm transition-colors"
              >
                Move
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="text-danger hover:bg-danger/10 focus-ring rounded-lg px-3 py-2 text-sm transition-colors"
              >
                Delete
              </button>
            </div>
            <CardDetailsScryfallLink set={selectedCard.card.set} collectorNumber={selectedCard.card.collector_number} />
          </footer>

          <EditCollectionCardModal
            isOpen={editPhase !== null}
            onClose={() => setEditPhase(null)}
            card={selectedCard}
            initialPhase={editPhase ?? 'details'}
          />

          {/* Folder Picker Modal */}
          <FolderPickerModal
            isOpen={showFolderPicker}
            onClose={() => setShowFolderPicker(false)}
            currentFolderId={selectedCard.folder_id}
            onSelect={handleMoveToFolder}
            isPending={moveMutation.isPending}
          />

          {/* Delete Confirmation Modal */}
          <ConfirmActionModal
            isOpen={showDeleteConfirm}
            onClose={() => setShowDeleteConfirm(false)}
            title="Delete Card"
            description={
              <>
                Are you sure you want to delete <span className="text-pearl font-medium">{selectedCard.card.name}</span> from your
                collection?
              </>
            }
            secondaryText="This action cannot be undone."
            confirmLabel="Delete"
            pendingLabel="Deleting..."
            isPending={deleteMutation.isPending}
            onConfirm={handleDelete}
          />
        </>
      )}
    </CardDetailsPanelLayout>
  );
}
